import { SHEET_BODY_CLASS } from "@/components/ui/sheetChrome";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface SheetBodySkeletonProps {
  /** Cuántas filas se reservan. Tres o cuatro es lo que se ve sin desplazar. */
  rows?: number;
  /** Una tarjeta grande arriba, para las hojas que abren con un bloque de cabecera. */
  card?: boolean;
  /**
   * El relleno del cuerpo de la hoja. Va puesto por defecto porque lo normal es
   * usar el esqueleto EN LUGAR del cuerpo entero. Se apaga (`padded={false}`)
   * cuando se mete DENTRO de un cuerpo que ya lo trae —FormSheet y GlassSheet lo
   * ponen ellos—, porque si no el esqueleto queda 18 px más estrecho que el
   * contenido al que sustituye y al llegar los datos se ve el salto lateral.
   */
  padded?: boolean;
  /**
   * Lo que se ANUNCIA mientras se espera, para quien no ve las barras grises.
   *
   * Se pasa allí donde el esqueleto sustituyó a un aviso de carga que SÍ se leía
   * («Cargando tarea…», «Cargando…»): sin esto, esa región se quedaba muda hasta
   * que llegaban los datos. Donde el esqueleto solo tapa la subida de la hoja
   * —dos décimas y media, con la cabecera de la ficha ya leída— se deja sin
   * poner: anunciar una espera que no da tiempo ni a oírse es ruido.
   */
  label?: string;
  className?: string;
}

/**
 * El esqueleto del cuerpo de una hoja, mientras la hoja sube.
 *
 * Es la otra mitad de `useSheetSettled`: ese decide CUÁNDO se pinta el contenido
 * de verdad y esto es lo que se enseña hasta entonces. Existe como pieza
 * compartida porque hasta ahora cada hoja se escribía el suyo a mano —tres
 * `Skeleton` de `h-20`, dos de `h-16`, un `Cargando...` de texto pelado en la
 * ficha del sector— y el resultado era que la misma aplicación cargaba de tres
 * maneras distintas según la hoja que abrieras.
 *
 * Las medidas no son decorativas: son las que ya se usaban en la aplicación
 * (`h-20 rounded-2xl` para una tarjeta de lista, `h-16` para una fila) para que
 * el hueco reservado se parezca a lo que va a caer encima y el cambio no se lea
 * como un salto sino como un relleno.
 *
 * Las barras van `aria-hidden` porque son puro adorno: anunciarlas sería
 * anunciar cajas vacías. Pero `aria-hidden` a secas dejó mudas a las dos
 * pantallas donde este esqueleto sustituyó a un «Cargando…» de verdad, así que
 * el aviso se pasa por `label` y se lee sin verse (`sr-only` + `role="status"`).
 *
 * NADA CARO AQUÍ DENTRO: solo `animate-pulse`, que es una opacidad y la resuelve
 * el compositor. Ni desenfoques ni sombras. La razón es la misma por la que el
 * velo de las hojas perdió su `backdrop-blur`: esto se pinta ENCIMA de una hoja
 * que se está moviendo, así que cualquier efecto que obligue a releer los
 * píxeles de debajo se recalcularía en cada fotograma de la subida — y se
 * cargaría justamente la animación que este componente existe para proteger.
 */
export function SheetBodySkeleton({
  rows = 4,
  card = false,
  padded = true,
  label,
  className,
}: SheetBodySkeletonProps) {
  // Un `rows` negativo o cero deja el esqueleto sin filas en vez de reventar.
  const rowCount = Math.max(0, Math.floor(rows));

  return (
    <div className={cn(padded && SHEET_BODY_CLASS, "space-y-3", className)}>
      {label && (
        <span role="status" className="sr-only">
          {label}
        </span>
      )}
      {/* El `aria-hidden` va aquí dentro y no en el contenedor: si envolviera
          también al aviso, lo escondería justo a quien lo necesita. */}
      <div aria-hidden="true" className="space-y-3">
        {card && <Skeleton className="h-28 w-full rounded-[18px]" />}
        {rowCount > 0 && (
          <div className="space-y-2.5">
            {Array.from({ length: rowCount }, (_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-2xl" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
